// src/modules/ida/components/HeartbeatPanel.tsx
//
// What? Sticky heartbeat management panel for the IDA chat view.
// Why? Surface active recurring heartbeats across the workspace so users
//      can always see + manage the scheduled runs that will affect them.
//      Users do not create heartbeats via UI; only list / edit / cancel.
// How? MUI Joy Sheet anchored by the chat layout. Admin-only (spec sec 10,
//      hard gate). Hidden entirely when the user is not admin or the
//      scheduler feature is not configured in the active environment.

import React, { useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Tooltip,
  Sheet,
  Typography,
  Divider,
  CircularProgress,
  Stack,
} from "@mui/joy";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import { useHeartbeats } from "../hooks/useHeartbeats";
import { HeartbeatSummary } from "../services/heartbeats";
import { HeartbeatEditor } from "./HeartbeatEditor";
import { formatHeartbeatNextFire } from "../utils/heartbeatTime";

const MAX_PER_CONVERSATION = 5;
const MAX_PER_USER = 15;

interface HeartbeatPanelProps {
  workspaceId: string | null;
  isAdmin: boolean;
}

function truncate(s: string | null, n = 40): string {
  if (!s) return "(no prompt)";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

export const HeartbeatPanel: React.FC<HeartbeatPanelProps> = ({
  workspaceId,
  isAdmin,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancellingTaskId, setCancellingTaskId] = useState<string | null>(null);

  const { items, schedulerAvailable, loading, error, refresh, cancel, edit } =
    useHeartbeats({
      workspaceId,
      enabled: isAdmin,
    });

  const handleCancel = useCallback(
    async (taskId: string) => {
      setCancelError(null);
      setCancellingTaskId(taskId);
      try {
        await cancel(taskId);
      } catch (err) {
        setCancelError(
          err instanceof Error ? err.message : "Could not cancel heartbeat.",
        );
      } finally {
        setCancellingTaskId(null);
      }
    },
    [cancel],
  );

  const count = items.length;
  const openItem = editingTaskId
    ? (items.find((it) => it.task_id === editingTaskId) ?? null)
    : null;

  // Hard gate: admin+ only. Hidden for everyone else per spec sec 10.
  if (!isAdmin) return null;

  // Keep the trigger hidden until the first non-empty heartbeat result.
  if (!schedulerAvailable || count === 0) return null;

  return (
    <>
      <ClickAwayListener onClickAway={() => setExpanded(false)}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Sheet
            variant="outlined"
            data-testid="heartbeat-panel"
            sx={{
              width: expanded
                ? { xs: "100%", sm: 420 }
                : { xs: "100%", sm: "fit-content" },
              maxWidth: "100%",
              ml: "auto",
              borderRadius: "sm",
              boxShadow: "sm",
              bgcolor: "background.surface",
              borderColor: "divider",
              px: 1.25,
              py: 1,
            }}
          >
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setExpanded((current) => !current)}
              data-testid="heartbeat-panel-toggle"
              sx={{
                width: "100%",
                px: 0.5,
                py: 0.25,
                minHeight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                textAlign: "left",
                color: "text.primary",
                "&:hover": { bgcolor: "transparent" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FavoriteIcon fontSize="small" sx={{ color: "primary.500" }} />
                <Typography level="title-sm">
                  Heartbeats {count}/{MAX_PER_CONVERSATION}
                </Typography>
              </Box>
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </Button>

            {expanded && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Divider />
                {cancelError && (
                  <Alert role="alert" color="danger">
                    Could not cancel heartbeat: {cancelError}
                  </Alert>
                )}
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Visible across this workspace. Max {MAX_PER_CONVERSATION} per
                  chat, {MAX_PER_USER} across your account.
                </Typography>

                {loading && (
                  <Box
                    sx={{
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CircularProgress size="sm" />
                    <Typography level="body-sm">Loading…</Typography>
                  </Box>
                )}

                {!loading && error && (
                  <Box sx={{ py: 1 }}>
                    <Typography
                      level="body-sm"
                      sx={{ color: "danger.plainColor" }}
                    >
                      {error}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{ maxHeight: 280, overflowY: "auto" }}
                  data-testid="heartbeat-panel-list"
                >
                  {items.map((hb: HeartbeatSummary) => (
                    <Box
                      key={hb.task_id}
                      sx={{
                        py: 1.25,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        "& + &": {
                          borderTop: "1px solid",
                          borderColor: "divider",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          level="body-sm"
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {truncate(hb.prompt)}
                        </Typography>
                        <Typography
                          level="body-xs"
                          sx={{ color: "text.tertiary" }}
                        >
                          Next: {formatHeartbeatNextFire(hb.next_fire_at)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 0.5, ml: 1, flexShrink: 0 }}
                      >
                        <Tooltip title="Edit">
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => {
                              setExpanded(false);
                              setEditingTaskId(hb.task_id);
                            }}
                            data-testid={`heartbeat-edit-${hb.task_id}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel heartbeat">
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            loading={cancellingTaskId === hb.task_id}
                            disabled={cancellingTaskId !== null}
                            onClick={() => handleCancel(hb.task_id)}
                            data-testid={`heartbeat-cancel-${hb.task_id}`}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Stack>
            )}
          </Sheet>
        </Box>
      </ClickAwayListener>

      {openItem && workspaceId && (
        <HeartbeatEditor
          open
          workspaceId={workspaceId}
          task={openItem}
          onSave={async (payload) => {
            await edit(openItem.task_id, payload);
            setEditingTaskId(null);
            await refresh();
          }}
          onClose={() => setEditingTaskId(null)}
        />
      )}
    </>
  );
};
